import { Controller } from "@hotwired/stimulus"
import { createHereMap, disposeHereMap, singleBubble } from "../helpers/here_map_helpers"

// Opens a modal HERE map pinned to a single photo's location. Used by the progression
// photo grid and by the EXIF panel of a document-folder file row.
// Coordinates are passed via Stimulus action params on the clicked element.
export default class extends Controller {
  open(event) {
    // One controller instance backs an entire photo grid, so a second open before the first
    // dialog closes would overwrite this.dialog and orphan the previous one — leaving it in the
    // DOM with its map, and therefore its WebGL context, never released.
    if (this.dialog) return

    const { latitude, longitude, title, takenAt } = event.params
    const lat = parseFloat(latitude)
    const lng = parseFloat(longitude)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return

    const dialog = this.buildDialog()
    document.body.appendChild(dialog)
    // Show first: HERE measures the container once at construction, so the map element
    // has to be laid out before createHereMap runs.
    dialog.showModal()

    createHereMap(dialog.querySelector("[data-map]"), { center: { lat, lng }, zoom: 16 })
      .then((context) => {
        // The dialog can be closed while the engine is still loading. Handing the map to
        // renderMarker then would store it on a controller that has already torn down, so
        // nothing would ever dispose it.
        if (this.dialog !== dialog) {
          disposeHereMap(context.map)
          return
        }
        this.renderMarker(context, { lat, lng }, title, takenAt)
      })
      .catch((error) => console.error("HERE map failed to load", error))
  }

  disconnect() {
    this.closeDialog()
  }

  buildDialog() {
    const dialog = document.createElement("dialog")
    dialog.className =
      "fixed left-1/2 top-1/2 m-0 -translate-x-1/2 -translate-y-1/2 rounded-lg p-0 w-11/12 max-w-2xl backdrop:bg-black/50"

    const header = document.createElement("div")
    header.className = "flex items-center justify-between border-b px-4 py-3"

    const heading = document.createElement("h3")
    heading.className = "text-sm font-semibold text-gray-900"
    heading.textContent = this.modalTitle

    const closeButton = document.createElement("button")
    closeButton.type = "button"
    closeButton.className = "text-gray-400 hover:text-gray-600"
    closeButton.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    `

    const mapElement = document.createElement("div")
    mapElement.dataset.map = ""
    mapElement.className = "h-96 w-full"

    header.append(heading, closeButton)
    dialog.append(header, mapElement)

    closeButton.addEventListener("click", () => this.closeDialog())
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) this.closeDialog()
    })
    // Covers Escape too, which closes the dialog without going through our handlers.
    dialog.addEventListener("close", () => this.teardown())

    this.dialog = dialog
    return dialog
  }

  renderMarker({ H, map, ui }, position, title, takenAt) {
    this.map = map

    const marker = new H.map.Marker(position)
    map.addObject(marker)

    const lines = [title, takenAt].filter(Boolean)
    if (!lines.length) return

    // Filename and formatted timestamp are user-controlled — build the bubble from DOM
    // nodes so neither can reach innerHTML.
    const content = document.createElement("div")
    content.className = "text-sm"
    lines.forEach((line) => {
      const row = document.createElement("div")
      row.textContent = line
      content.appendChild(row)
    })

    singleBubble(H, ui)(position, content)
  }

  closeDialog() {
    if (!this.dialog) return
    // Fires the "close" listener, which tears the map down and removes the element.
    this.dialog.close()
  }

  // Releasing the engine matters here: a new map is created on every open, so leaving
  // the previous one alive leaks a WebGL context per click.
  teardown() {
    disposeHereMap(this.map)
    this.map = null
    if (this.dialog) {
      this.dialog.remove()
      this.dialog = null
    }
  }

  get modalTitle() {
    return this.element.dataset.photoLocationMapModalTitle || ""
  }
}
